import { useEffect, useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { loadCurrentViewer } from 'hooks/use-current-roles';
import type { NavAudience } from './nav-config';

/**
 * Số việc ĐANG CHỜ người quản lý xử lý — hiện thành số đỏ trên thanh bên và thành
 * thẻ "Cần xử lý" ở trang Tổng quan. Chỉ dùng API mà cả ADMIN lẫn MOD gọi được
 * (MOD gọi API chỉ-ADMIN sẽ nhận 403).
 *
 * Mỗi nguồn gọi `limit=1` và chỉ đọc tổng số → rất nhẹ. KHÔNG dùng
 * /stars/admin/dashboard ở đây vì mỗi lần gọi nó đối soát toàn bộ lịch sử giao dịch.
 */
export interface PendingSource {
  /** key menu trong nav-config — số hiện cạnh mục này. */
  key: string;
  label: string;
  /** Mở danh sách đã lọc sẵn "đang chờ". */
  to: string;
  url: string;
  params: Record<string, string | number>;
  /** Ai gọi nguồn này (SPEC Studio §7.5). Không khai = ADMIN và MOD (như cũ); Quản lý Studio không gọi. */
  audience?: NavAudience[];
  /** Cách đọc tổng khi API không trả `total` (nhận body của phản hồi). */
  read?: (body: any) => number | undefined;
  /** Tham số truy vấn thêm theo người xem (nguồn `studio` gửi studioId). Ghép sau `params`. */
  viewerParams?: (viewer: PendingViewer) => Record<string, string | number>;
  /** Mục menu nhận số này; không khai = `key`. Nhiều nguồn cùng `menuKey` thì cộng lại. */
  menuKey?: string;
}

export interface PendingViewer {
  audience: NavAudience | null;
  studioId: number | null;
}

/** Nguồn này có gọi cho người xem đó không. Chưa biết người xem (null) → như MOD (hành vi cũ). */
export const sourceAllowedFor = (source: PendingSource, audience: NavAudience | null) =>
  (source.audience ?? ['ADMIN', 'MOD']).includes(audience ?? 'MOD');

export const PENDING_SOURCES: PendingSource[] = [
  {
    // GET /support/admin/summary trả { total = số người đang chờ trả lời, ... }.
    // Chưa chạy migration hỗ trợ → 503 → nguồn này bị bỏ qua (không hiện số sai).
    key: 'support',
    label: 'Tin hỗ trợ chờ trả lời',
    to: '/support?box=waiting',
    url: API.supportSummary,
    params: {},
    audience: ['ADMIN', 'MOD', 'STUDIO'],
  },
  {
    key: 'admin/attendance/leave-requests',
    label: 'Đơn nghỉ phép chờ duyệt',
    to: '/admin/attendance/leave-requests?status=PENDING',
    url: API.leaveRequests,
    params: { status: 'PENDING', page: 1, limit: 1 },
  },
  {
    key: 'overtime-management',
    label: 'Đơn tăng ca chờ duyệt',
    to: '/overtime-management?status=PENDING',
    url: API.overtimeRequests,
    params: { status: 'PENDING', page: 1, limit: 1 },
  },
  {
    key: 'star/shop',
    label: 'Đơn đổi quà chờ giao',
    to: '/star/shop?tab=orders',
    url: API.starsOrders,
    params: { status: 'PENDING', page: 1, limit: 1 },
  },
  {
    key: 'star/missions',
    label: 'Báo cáo nhiệm vụ chờ duyệt',
    to: '/star/missions?tab=reviews',
    url: API.starsMissionReviews,
    params: { status: 'PENDING_REVIEW', page: 1, limit: 1 },
  },
  {
    key: 'star/penalties',
    label: 'Yêu cầu xoá phạt chờ duyệt',
    to: '/star/penalties?tab=waives',
    url: API.starsPenalties,
    params: { status: 'WAIVE_PENDING', page: 1, limit: 1 },
  },
  {
    // GET /feed/admin/summary trả { total = nội dung bị báo cáo đang chờ + bài/bình luận
    // đang giữ + bài/bình luận "Cần xem", ... } — MOD gọi được (docs/bang-tin/03-admin.md §4.4).
    // Chưa chạy migration bảng tin → 503, backend chưa có module → 404: nguồn bị bỏ qua.
    key: 'feed',
    label: 'Bảng tin: báo cáo & nội dung cần xem',
    to: '/feed?tab=reports',
    url: API.feedAdminSummary,
    params: {},
  },
  // Studio (docs/studio-quan-ly/SPEC-TRIEN-KHAI-STUDIO.md §7.5). Backend chưa có /studios → 404: bỏ qua.
  {
    key: 'studios',
    label: 'Đề nghị từ Studio chờ duyệt',
    to: '/studios?tab=requests',
    url: API.studioRequestsSummary,
    params: {},
    audience: ['ADMIN'],
  },
  {
    key: 'studio-monitor',
    menuKey: 'studios',
    label: 'Cờ Studio cần xem',
    to: '/studios?tab=monitor',
    url: API.studioMonitorSummary,
    params: {},
    audience: ['ADMIN'],
  },
  {
    // Cùng công thức `tabActionable` với ô Tổng quan của Studio: chỉ LEAVE + CLAIM (STUDIO_TAB_KINDS).
    // Backend cũ chưa có `actionableByKind` → 0, không ra số sai.
    key: 'studio',
    label: 'Việc cần xử lý của Studio',
    to: '/studio?tab=queue',
    url: API.approvalsSummary,
    params: {},
    viewerParams: (viewer): Record<string, number> =>
      viewer.studioId ? { studioId: viewer.studioId } : {},
    audience: ['STUDIO'],
    read: (body) => (body?.actionableByKind?.LEAVE ?? 0) + (body?.actionableByKind?.CLAIM ?? 0),
  },
];

export interface PendingCounts {
  loaded: boolean;
  /**
   * key MENU → số việc đang chờ (nguồn cùng `menuKey` đã cộng lại). Nguồn lỗi thì không có key
   * (không hiện số sai).
   */
  byKey: Record<string, number>;
  /** key NGUỒN → số việc (thẻ "Cần xử lý" ở Tổng quan). Chỉ có nguồn đã gọi được cho người xem này. */
  bySource: Record<string, number>;
  refresh: () => void;
}

const readTotal = (res: any): number | undefined => {
  const body = res?.data ?? res;
  const total = body?.metadata?.total ?? body?.meta?.total ?? body?.total;
  return typeof total === 'number' ? total : undefined;
};

const readSource = (source: PendingSource, res: any): number | undefined => {
  if (!source.read) return readTotal(res);
  const value = source.read(res?.data ?? res);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

type Snapshot = {
  loaded: boolean;
  byKey: Record<string, number>;
  bySource: Record<string, number>;
};

// Kho dùng chung: thanh bên + trang Tổng quan cùng đọc, chỉ gọi API một lần.
let snapshot: Snapshot = { loaded: false, byKey: {}, bySource: {} };
let snapshotViewer = '';
let inflight: Promise<void> | null = null;
let lastFetch = 0;
const listeners = new Set<() => void>();
const REFRESH_MS = 2 * 60 * 1000;

async function fetchAll(force = false) {
  if (inflight) return inflight;
  inflight = (async () => {
    // Nguồn lọc theo người xem (Quản lý Studio không gọi nguồn đếm toàn công ty).
    const viewer = await loadCurrentViewer().catch(
      (): PendingViewer => ({ audience: null, studioId: null })
    );
    const viewerKey = `${viewer.audience ?? ''}:${viewer.studioId ?? ''}`;
    const fresh = Date.now() - lastFetch < REFRESH_MS && snapshot.loaded;
    // Đổi tài khoản trong cùng tab → người xem khác → gọi lại dù số còn mới.
    if (!force && fresh && viewerKey === snapshotViewer) return;
    const sources = PENDING_SOURCES.filter((source) => sourceAllowedFor(source, viewer.audience));
    const results = await Promise.all(
      sources.map((source) =>
        AxiosUtils.get(source.url, { ...source.params, ...source.viewerParams?.(viewer) })
          .then((res) => [source, readSource(source, res)] as const)
          .catch(() => [source, undefined] as const)
      )
    );
    const byKey: Record<string, number> = {};
    const bySource: Record<string, number> = {};
    results.forEach(([source, total]) => {
      if (typeof total !== 'number') return;
      bySource[source.key] = total;
      const menuKey = source.menuKey ?? source.key;
      byKey[menuKey] = (byKey[menuKey] ?? 0) + total;
    });
    snapshot = { loaded: true, byKey, bySource };
    snapshotViewer = viewerKey;
    lastFetch = Date.now();
    listeners.forEach((listener) => listener());
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Gọi sau khi duyệt/giao xong để số trên menu cập nhật ngay. */
export const refreshPendingCounts = () => fetchAll(true);

export function usePendingCounts(): PendingCounts {
  const [state, setState] = useState(snapshot);

  useEffect(() => {
    const listener = () => setState(snapshot);
    listeners.add(listener);
    fetchAll();
    const timer = window.setInterval(() => fetchAll(), REFRESH_MS);
    return () => {
      listeners.delete(listener);
      window.clearInterval(timer);
    };
  }, []);

  return { ...state, refresh: refreshPendingCounts };
}
